export interface IdName {
  id: number;
  name: string;
}

/**
 * Coerce a `fetch-id-name` response into `{ id, name }` pairs.
 *
 * The five hierarchy routes each answer in a different shape — a bare array, a
 * wrapped object (`{ data | content | items | <named> }`), or a plain id↔name
 * record in either orientation — and none of them declare a
 * `transformResponse`. Rather than teach every caller all five shapes, read
 * them all here.
 *
 * @param namedKey the collection key some routes wrap the array in ("groups").
 * @param idField the id column for this level ("groupId"); `id` is always
 * accepted too, since the slim routes are inconsistent about which they send.
 */
export function toIdNamePairs(response: unknown, namedKey: string, idField: string): IdName[] {
  if (Array.isArray(response)) return fromArray(response, idField);

  if (response && typeof response === "object") {
    const record = response as Record<string, unknown>;
    const arr =
      pickArray(record.data) ??
      pickArray(record.content) ??
      pickArray(record.items) ??
      pickArray(record[namedKey]);
    if (arr) return fromArray(arr, idField);

    const flat = record.data && typeof record.data === "object" ? record.data : record;
    return fromRecord(flat as Record<string, unknown>);
  }

  return [];
}

/**
 * Parse a slim id-name response and drop repeats.
 *
 * The scoped routes join through the level below, so a division with four
 * locations comes back four times. React reports the repeat as two children
 * with the same key and drops one, and a name lookup only ever wants one row
 * per id — so dedupe at the endpoint, where every consumer of the hook gets it.
 */
export function toUniqueIdNamePairs(
  response: unknown,
  namedKey: string,
  idField: string
): IdName[] {
  const byId = new Map<number, IdName>();
  for (const row of toIdNamePairs(response, namedKey, idField)) {
    if (!byId.has(row.id)) byId.set(row.id, row);
  }
  return [...byId.values()];
}

/** Index a `fetch-id-name` response by id, for name lookups. */
export function toIdNameMap(
  response: unknown,
  namedKey: string,
  idField: string
): Map<number, string> {
  const map = new Map<number, string>();
  for (const row of toIdNamePairs(response, namedKey, idField)) map.set(row.id, row.name);
  return map;
}

function pickArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

function fromArray(rows: unknown[], idField: string): IdName[] {
  const out: IdName[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const rec = row as Record<string, unknown>;
    const id = numericId(rec[idField] ?? rec.id);
    const name = typeof rec.name === "string" ? rec.name.trim() : "";
    if (id != null && name) out.push({ id, name });
  }
  return out;
}

function fromRecord(record: Record<string, unknown>): IdName[] {
  const out: IdName[] = [];
  for (const [key, value] of Object.entries(record)) {
    const keyAsId = Number(key);
    if (typeof value === "number" && Number.isFinite(value) && Number.isNaN(keyAsId)) {
      out.push({ id: value, name: key });
    } else if (typeof value === "string" && Number.isFinite(keyAsId)) {
      out.push({ id: keyAsId, name: value });
    }
  }
  return out;
}

function numericId(value: unknown): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}
